import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EnvelopeSimple } from '@phosphor-icons/react'

import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/primitives'
import { PinDayLogo } from '@/components/ui/PinDayLogo'

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const isValid = useMemo(() => email.trim().length > 0, [email])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!isValid) {
        toast.error(t('errors.required'))
        return
      }

      setIsLoading(true)
      try {
        await api.requestPasswordReset(email)
        toast.success(t('resetPassword.success'))
        navigate('/login')
      } catch (error) {
        // Intentionally generic (avoid user enumeration); backend also returns ok.
        toast.success(t('resetPassword.success'))
        navigate('/login')
      } finally {
        setIsLoading(false)
      }
    },
    [email, isValid, navigate, t],
  )

  return (
    <div className="min-h-screen bg-[var(--pd-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center">
            <PinDayLogo size="xl" />
          </Link>
          <p className="text-[var(--pd-foreground)]/60 mt-2">{t('resetPassword.subtitle')}</p>
        </div>

        <Card>
          <div className="p-8">
            <h1 className="text-xl font-semibold text-[var(--pd-foreground)]">{t('resetPassword.title')}</h1>

            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              <div className="space-y-2">
                <Label htmlFor="email">{t('resetPassword.email')}</Label>
                <div className="relative">
                  <EnvelopeSimple
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('resetPassword.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[var(--pd-primary)] hover:bg-[var(--pd-primary)]/90 text-white py-6 rounded-xl font-semibold text-base shadow-lg transition-all duration-200"
              >
                {isLoading ? t('resetPassword.submitting') : t('resetPassword.submit')}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-[var(--pd-primary)] hover:text-[var(--pd-primary)]/80 font-semibold transition-colors"
                >
                  {t('resetPassword.backToLogin')}
                </Link>
              </div>
            </form>
          </div>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-[var(--pd-foreground)]/60 hover:text-[var(--pd-foreground)] transition-colors"
          >
            ← {t('common:actions.back')}
          </Link>
        </div>
      </div>
    </div>
  )
}
